import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Message,
  SelectPicker,
  Schema,
} from 'rsuite';
import {
  addDays,
  addWeeks,
  endOfMonth,
  format,
  getDay,
  startOfMonth,
  subDays,
} from 'date-fns';

const { StringType, NumberType } = Schema.Types;

const recurrenceOptions = [
  { label: 'Fixed Date', value: 'fixed' },
  { label: 'First Weekday of Month', value: 'first_weekday' },
  { label: 'Nth Weekday of Month', value: 'nth_weekday' },
  { label: 'Last Weekday of Month', value: 'last_weekday' },
  { label: 'Nth Week of Month', value: 'nth_week' },
];

const monthOptions = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const weekdayOptions = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
];

const nthOptions = [
  { label: '1st', value: 1 },
  { label: '2nd', value: 2 },
  { label: '3rd', value: 3 },
  { label: '4th', value: 4 },
  { label: '5th', value: 5 },
];

const weekendAdjustmentOptions = [
  { label: 'None', value: 'none' },
  { label: 'Weekend Observed (Sat→Fri, Sun→Mon)', value: 'nearest_weekday' },
  { label: 'Preceding Friday', value: 'preceding_friday' },
  { label: 'Following Monday', value: 'following_monday' },
];

const applyWeekendAdjustment = (date, adjustment) => {
  const day = getDay(date);

  if (adjustment === 'preceding_friday') {
    if (day === 0) return subDays(date, 2);
    if (day === 6) return subDays(date, 1);
  }

  if (adjustment === 'following_monday') {
    if (day === 6) return addDays(date, 2);
    if (day === 0) return addDays(date, 1);
  }

  if (adjustment === 'nearest_weekday') {
    if (day === 6) return subDays(date, 1);
    if (day === 0) return addDays(date, 1);
  }

  return date;
};

const resolveRawStartDate = (rule, year) => {
  const recurrenceType = String(rule?.recurrence_type || '').toLowerCase();
  const month = Number(rule?.month);
  const day = Number(rule?.day);
  const weekday = Number(rule?.weekday);
  const n = Number(rule?.n);

  if (!Number.isInteger(month) || month < 1 || month > 12) return null;

  if (recurrenceType === 'fixed') {
    if (!Number.isInteger(day) || day < 1 || day > 31) return null;
    return new Date(year, month - 1, day);
  }

  if (recurrenceType === 'nth_weekday') {
    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) return null;
    if (!Number.isInteger(n) || n < 1 || n > 5) return null;

    const firstOfMonth = startOfMonth(new Date(year, month - 1, 1));
    const firstDay = getDay(firstOfMonth);
    const offset = (weekday - firstDay + 7) % 7;
    const firstOccurrence = addDays(firstOfMonth, offset);
    return addWeeks(firstOccurrence, n - 1);
  }

  if (recurrenceType === 'first_weekday') {
    if (!Number.isInteger(weekday) || weekday < 1 || weekday > 5) return null;

    const firstOfMonth = startOfMonth(new Date(year, month - 1, 1));
    const firstDay = getDay(firstOfMonth);
    const offset = (weekday - firstDay + 7) % 7;
    return addDays(firstOfMonth, offset);
  }

  if (recurrenceType === 'last_weekday') {
    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) return null;

    let cursor = endOfMonth(new Date(year, month - 1, 1));
    while (getDay(cursor) !== weekday) {
      cursor = subDays(cursor, 1);
    }
    return cursor;
  }

  if (recurrenceType === 'nth_week') {
    if (!Number.isInteger(n) || n < 1 || n > 5) return null;

    const firstOfMonth = startOfMonth(new Date(year, month - 1, 1));
    const firstDay = getDay(firstOfMonth);
    const mondayOffset = (1 - firstDay + 7) % 7;
    const firstMonday = addDays(firstOfMonth, mondayOffset);
    return addWeeks(firstMonday, n - 1);
  }

  return null;
};

const resolveDateRange = (rule, year) => {
  const rawStart = resolveRawStartDate(rule, year);
  if (!rawStart) return { startDate: null, endDate: null };

  const recurrenceType = String(rule?.recurrence_type || '').toLowerCase();
  const durationDays = Math.max(1, Number(rule?.duration_days) || 1);
  const adjustment = String(rule?.weekend_adjustment || 'none').toLowerCase();

  const startDate = recurrenceType === 'nth_week' ? rawStart : applyWeekendAdjustment(rawStart, adjustment);
  const endDate = addDays(startDate, durationDays - 1);

  return { startDate, endDate };
};

const Textarea = React.forwardRef(function Textarea({ value, onChange, className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={(event) => onChange?.(event.target.value, event)}
      className={['rs-input', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

const getInitialFormValue = (initialValues = {}) => ({
  name: initialValues.name || '',
  recurrence_type: initialValues.recurrence_type || 'fixed',
  month: Number.isInteger(initialValues.month) ? initialValues.month : 1,
  day: Number.isInteger(initialValues.day) ? initialValues.day : null,
  weekday: Number.isInteger(initialValues.weekday) ? initialValues.weekday : null,
  n: Number.isInteger(initialValues.n) ? initialValues.n : null,
  duration_days: Number.isInteger(initialValues.duration_days)
    ? initialValues.duration_days
    : (initialValues.recurrence_type === 'nth_week' ? 5 : 1),
  weekend_adjustment: initialValues.weekend_adjustment || 'none',
  notes: initialValues.notes || '',
});

export default function CloseDateForm({ onSubmit, loading = false, error, initialValues }) {
  const [formValue, setFormValue] = useState(() => getInitialFormValue(initialValues));
  const [localError, setLocalError] = useState('');

  const model = useMemo(
    () =>
      Schema.Model({
        name: StringType().isRequired('Name is required'),
        recurrence_type: StringType().isRequired('Recurrence type is required'),
        month: NumberType().isRequired('Month is required'),
      }),
    []
  );

  const recurrenceType = String(formValue.recurrence_type || '').toLowerCase();
  const isFixed = recurrenceType === 'fixed';
  const isFirstWeekday = recurrenceType === 'first_weekday';
  const isNthWeekday = recurrenceType === 'nth_weekday';
  const isLastWeekday = recurrenceType === 'last_weekday';
  const isNthWeek = recurrenceType === 'nth_week';

  const preview = useMemo(() => {
    const thisYear = new Date().getFullYear();
    const nextYear = thisYear + 1;
    const current = resolveDateRange(formValue, thisYear);
    const next = resolveDateRange(formValue, nextYear);

    const formatRange = ({ startDate, endDate }) => {
      if (!startDate || !endDate) return '—';
      const startText = format(startDate, 'MMM d, yyyy');
      const endText = format(endDate, 'MMM d, yyyy');
      return startText === endText ? startText : `${startText} → ${endText}`;
    };

    return {
      thisYear,
      nextYear,
      thisYearRange: formatRange(current),
      nextYearRange: formatRange(next),
    };
  }, [formValue]);

  const handleSubmit = async () => {
    setLocalError('');

    if (!formValue.name || !formValue.recurrence_type || !formValue.month) {
      setLocalError('Please fill in all required fields.');
      return;
    }

    if (isFixed && !formValue.day) {
      setLocalError('Day is required for fixed recurrence.');
      return;
    }

    if (isFirstWeekday && (!formValue.weekday && formValue.weekday !== 0)) {
      setLocalError('Weekday is required for first weekday recurrence.');
      return;
    }

    if (isNthWeekday && (!formValue.weekday && formValue.weekday !== 0)) {
      setLocalError('Weekday is required for nth weekday recurrence.');
      return;
    }

    if (isNthWeekday && !formValue.n) {
      setLocalError('Occurrence is required for nth weekday recurrence.');
      return;
    }

    if (isLastWeekday && (!formValue.weekday && formValue.weekday !== 0)) {
      setLocalError('Weekday is required for last weekday recurrence.');
      return;
    }

    if (isNthWeek && !formValue.n) {
      setLocalError('Which week is required for nth week recurrence.');
      return;
    }

    const payload = {
      name: formValue.name.trim(),
      recurrence_type: formValue.recurrence_type,
      month: Number(formValue.month),
      day: isFixed ? Number(formValue.day) : null,
      weekday: isFirstWeekday || isNthWeekday || isLastWeekday ? Number(formValue.weekday) : null,
      n: isNthWeekday || isNthWeek ? Number(formValue.n) : null,
      duration_days: Math.max(1, Number(formValue.duration_days) || 1),
      weekend_adjustment: isNthWeek ? 'none' : formValue.weekend_adjustment,
      notes: formValue.notes?.trim() || null,
      is_active: true,
    };

    await onSubmit?.(payload);
  };

  return (
    <div>
      {error ? (
        <Message type="error" showIcon header="Error" style={{ marginBottom: 12 }}>
          {String(error?.message || error)}
        </Message>
      ) : null}

      {localError ? (
        <Message type="error" showIcon style={{ marginBottom: 12 }}>
          {localError}
        </Message>
      ) : null}

      <Form fluid model={model} formValue={formValue} onChange={setFormValue}>
        <Form.Group controlId="name">
          <Form.ControlLabel>Name</Form.ControlLabel>
          <Form.Control name="name" accepter={Input} />
        </Form.Group>

        <Form.Group controlId="recurrence_type">
          <Form.ControlLabel>Recurrence Type</Form.ControlLabel>
          <Form.Control
            name="recurrence_type"
            accepter={SelectPicker}
            data={recurrenceOptions}
            cleanable={false}
            searchable={false}
          />
        </Form.Group>

        <div className="d-flex gap-2" style={{ marginBottom: 22, minWidth: 0 }}>
          <Form.Group controlId="month" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
            <Form.ControlLabel>Month</Form.ControlLabel>
            <Form.Control
              name="month"
              accepter={SelectPicker}
              data={monthOptions}
              cleanable={false}
              searchable={false}
              style={{ width: '100%' }}
            />
          </Form.Group>

          {isFixed ? (
            <Form.Group controlId="day" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
              <Form.ControlLabel>Day</Form.ControlLabel>
              <Form.Control name="day" accepter={InputNumber} min={1} max={31} style={{ width: '100%' }} />
            </Form.Group>
          ) : null}
        </div>

        {isFirstWeekday ? (
          <Form.Group controlId="weekday">
            <Form.ControlLabel>Weekday</Form.ControlLabel>
            <Form.Control name="weekday" accepter={SelectPicker} data={weekdayOptions} cleanable={false} searchable={false} />
          </Form.Group>
        ) : null}

        {isNthWeekday ? (
          <>
            <Form.Group controlId="weekday">
              <Form.ControlLabel>Weekday</Form.ControlLabel>
              <Form.Control name="weekday" accepter={SelectPicker} data={weekdayOptions} cleanable={false} searchable={false} />
            </Form.Group>
            <Form.Group controlId="n">
              <Form.ControlLabel>Which occurrence</Form.ControlLabel>
              <Form.Control name="n" accepter={SelectPicker} data={nthOptions} cleanable={false} searchable={false} />
            </Form.Group>
          </>
        ) : null}

        {isLastWeekday ? (
          <Form.Group controlId="weekday">
            <Form.ControlLabel>Weekday</Form.ControlLabel>
            <Form.Control name="weekday" accepter={SelectPicker} data={weekdayOptions} cleanable={false} searchable={false} />
          </Form.Group>
        ) : null}

        {isNthWeek ? (
          <>
            <Form.Group controlId="n">
              <Form.ControlLabel>Which week</Form.ControlLabel>
              <Form.Control name="n" accepter={SelectPicker} data={nthOptions} cleanable={false} searchable={false} />
            </Form.Group>

            <Form.Group controlId="duration_days">
              <Form.ControlLabel>Duration (days)</Form.ControlLabel>
              <Form.Control name="duration_days" accepter={InputNumber} min={1} max={31} />
            </Form.Group>
          </>
        ) : null}

        {!isNthWeek ? (
          <Form.Group controlId="weekend_adjustment">
            <Form.ControlLabel>Weekend Adjustment</Form.ControlLabel>
            <Form.Control
              name="weekend_adjustment"
              accepter={SelectPicker}
              data={weekendAdjustmentOptions}
              cleanable={false}
              searchable={false}
            />
          </Form.Group>
        ) : null}

        <Form.Group controlId="notes">
          <Form.ControlLabel>Notes</Form.ControlLabel>
          <Form.Control name="notes" accepter={Textarea} rows={3} />
        </Form.Group>

        <div className="border rounded p-3 mb-3">
          <div className="fw-semibold mb-2">Live Preview</div>
          <div className="d-flex flex-column gap-1">
            <div>
              <span className="text-muted me-2">{preview.thisYear}:</span>
              <span>{preview.thisYearRange}</span>
            </div>
            <div>
              <span className="text-muted me-2">{preview.nextYear}:</span>
              <span>{preview.nextYearRange}</span>
            </div>
          </div>
        </div>

        <Button appearance="primary" onClick={handleSubmit} loading={loading}>
          Save
        </Button>
      </Form>
    </div>
  );
}
